<template>
  <button
    type="button"
    data-test-id="conversation-chat"
    :aria-current="isActiveRef ? 'page' : undefined"
    :class="
      cn(
        'flex w-full items-center gap-3 rounded-md border px-3 py-2.5 text-left text-foreground transition-colors',
        'focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-inset focus-visible:outline-none',
        isActiveRef ? 'border-border bg-accent' : 'hover:bg-accent/60 border-transparent',
      )
    "
    @click="goToConversation"
  >
    <Avatar class="size-10" data-test-id="conversation-avatar">
      <template v-if="conversation.groupId">
        <AvatarFallback class="conversation-group-avatar">
          <GroupIcon color="black" filled />
        </AvatarFallback>
      </template>
      <template v-else>
        <AvatarImage v-if="correspondentPictureRef" :src="correspondentPictureRef" alt="" />
        <AvatarFallback class="text-xs font-semibold uppercase">{{ correspondentInitialsRef }}</AvatarFallback>
      </template>
    </Avatar>

    <span class="flex min-w-0 flex-1 flex-col gap-0.5">
      <span
        v-if="programNameRef"
        :class="
          cn('truncate text-[0.6875rem] leading-tight font-semibold tracking-wide uppercase', programColorClassRef)
        "
        data-test-id="conversation-program"
        >{{ programNameRef }}</span
      >
      <span class="flex items-baseline gap-2">
        <span class="min-w-0 flex-1 truncate text-[0.9375rem] font-semibold">{{ conversationNameRef }}</span>
        <time
          v-if="lastMessageRef?.sentAt"
          :datetime="lastMessageRef.sentAt"
          class="text-muted-foreground shrink-0 text-xs tabular-nums"
          data-test-id="conversation-time"
          >{{ timeLabelRef }}</time
        >
      </span>
      <span class="flex items-center gap-2">
        <span
          :class="
            cn(
              'min-w-0 flex-1 truncate text-[0.8125rem]',
              unreadCountRef > 0 ? 'text-foreground font-medium' : 'text-muted-foreground',
            )
          "
          data-test-id="conversation-preview"
        >
          {{ previewTextRef || t('chat.conversationsList.noMessageYet') }}
        </span>
      </span>
    </span>
    <!-- non lus : pastille a droite, centree sur la ligne (maquette) ; un point
         pour une conversation jamais ouverte sans message a compter -->
    <span
      v-if="unreadCountRef > 0"
      class="bg-primary text-primary-foreground inline-flex h-5 min-w-5 shrink-0 self-center items-center justify-center rounded-full px-1.5 text-[0.6875rem] font-semibold tabular-nums"
      data-test-id="conversation-notification"
    >
      <span aria-hidden="true">{{ unreadCountRef > 99 ? '99+' : unreadCountRef }}</span>
      <span class="sr-only">{{ t('chat.conversationsList.unreadCount', { count: unreadCountRef }) }}</span>
    </span>
    <span
      v-else-if="hasNotificationRef"
      class="bg-primary size-2.5 shrink-0 self-center rounded-full"
      data-test-id="conversation-notification"
    >
      <span class="sr-only">{{ t('chat.conversationsList.unread') }}</span>
    </span>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import useAccount from '@/features/auth/hooks/use-account.hook'
import useGroupConversationsWithoutFirstMessage from '@/features/chat/hooks/use-group-conversations-without-first-message.hook'
import type { V2ConversationType } from '@/features/chat/types'
import { getConversationPreview, getConversationTimeLabel } from '@/features/chat/utils/conversation-preview'
import groupsStore from '@/features/groups/stores/groups/groups.store'
import useProgram from '@/features/programs/hooks/use-program.hook'
import programsStore from '@/features/programs/stores/programs/programs.store'
import GroupIcon from '@/features/ui/components/icons/group-icon.vue'
import useRouter from '@/hooks/use-router.hook'
import { useI18n } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { selectGetCurrentRoute } from '@/store/route/route.selector'
import routeStore from '@/store/route/route.store'
import { PROFILE_PICTURE_BUCKET_URL, ROUTES } from '@/utils/config/constants'
import { getProgramTextClass } from '@/utils/misc/color-palette'

const props = defineProps<{
  conversation: V2ConversationType
}>()

const { t } = useI18n()
const { accountRef } = useAccount()
const router = useRouter()

const currentRouteRef = selectGetCurrentRoute(routeStore)

const isActiveRef = computed(() => currentRouteRef.value?.params.roomId === props.conversation.conversationId)

const correspondentRef = computed(() => props.conversation.users.find((user) => user.id !== accountRef.value?.id))

const conversationNameRef = computed(() => {
  if (props.conversation.groupId) return props.conversation.groupName
  return `${correspondentRef.value?.firstName ?? ''} ${correspondentRef.value?.lastName ?? ''}`.trim()
})

// surtitre programme : uniquement sur les conversations de groupe
const programIdRef = computed(() => (props.conversation.groupId ? (props.conversation.programId ?? null) : null))
const { programRef } = useProgram(programIdRef)
const programNameRef = computed(() => programRef.value?.name ?? null)
const programColorClassRef = computed(() => (programRef.value ? getProgramTextClass(programRef.value) : ''))

const correspondentInitialsRef = computed(
  () => `${correspondentRef.value?.firstName?.[0] ?? ''}${correspondentRef.value?.lastName?.[0] ?? ''}`,
)

const correspondentPictureRef = computed(() =>
  correspondentRef.value?.picture ? PROFILE_PICTURE_BUCKET_URL + correspondentRef.value.picture : undefined,
)

// group has notifications
const { groupConversationsWithoutFirstMessageSentRef } = useGroupConversationsWithoutFirstMessage()
const hasNotificationRef = computed(() => {
  return (
    // check if the conversation is in the list of group conversations without first message sent
    groupConversationsWithoutFirstMessageSentRef.value.some(
      (c) => c.conversationId === props.conversation.conversationId,
    ) ||
    // check if the user has not seen the conversation
    !props.conversation.users.find((user) => user.id === accountRef.value?.id)?.hasSeen
  )
})

// apercu du dernier message, heure et compteur de non lus
const lastMessageRef = computed(() => props.conversation.messages.at(-1))
const isOwnLastMessageRef = computed(() => lastMessageRef.value?.userId === accountRef.value?.id)
const previewRef = computed(() =>
  getConversationPreview(lastMessageRef.value, props.conversation.users, {
    sessionRequest: t('chat.conversationsList.sessionRequest'),
    attachment: t('chat.conversationsList.attachment'),
  }),
)
const previewTextRef = computed(() => {
  if (!previewRef.value) return ''
  return isOwnLastMessageRef.value ? `${t('chat.conversationsList.you')} ${previewRef.value}` : previewRef.value
})
const timeLabelRef = computed(() =>
  getConversationTimeLabel(lastMessageRef.value?.sentAt, { yesterday: t('chat.conversationsList.yesterday') }),
)
const unreadCountRef = computed(() => (isActiveRef.value ? 0 : props.conversation.unreadCount))

// go to a specific conversation
function goToConversation() {
  const groupId = props.conversation.groupId ?? null
  const conversationId = props.conversation.conversationId

  if (!conversationId) {
    console.error('Impossible de naviguer, conversationId est manquant.')
    return
  }

  // chat tous programmes : on selectionne en coulisse le programme de la conversation
  // (sessions, reglages et planification suivent), avant le groupe que ce choix remet a zero
  const programId = props.conversation.programId
  if (programId && programId !== programsStore.selectedProgramId) {
    programsStore.setSelectedProgramId(programId)
  }

  if (groupsStore.selectedGroupId !== groupId) {
    groupsStore.setSelectedGroupId(groupId)
  } else {
    groupsStore.setSelectedGroupId(null)
    setTimeout(() => groupsStore.setSelectedGroupId(groupId), 0)
  }

  const chatRoute = ROUTES.CHAT(conversationId)

  router?.push(chatRoute).catch((error) => {
    console.error('Router navigation error:', error)
  })
}
</script>
